import React, { useCallback, useMemo, useState, useEffect } from "react";
import {
  Form,
  Modal,
  Button as RSuiteButton,
  Divider,
  Message,
  useToaster,
  InputNumber,
  Stack,
  Tooltip,
  Whisper,
} from "rsuite";
import axiosInstance from "../../../../../../appHelpers/axiosInstance";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Input, Picker, FormActions } from "../../../../../ui";
import { FilterSystems } from "../../Users/helpers/FilterableComponent";

/* ─────────────────────────────
   Helpers & constants
   ───────────────────────────── */
const DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const HALF_HOURS = Array.from({ length: 48 }, (_, i) => {
  const hh = String(Math.floor(i / 2)).padStart(2, "0");
  const mm = i % 2 ? "30" : "00";
  return `${hh}:${mm}`;
});
const EMAIL_SLOTS = DAYS.flatMap((d) => HALF_HOURS.map((t) => `${d}-${t}`));

function to12HrLabel(slot) {
  const [day, time] = slot.split("-");
  let [h, m] = time.split(":").map((n) => parseInt(n, 10));
  const ampm = h >= 12 ? "PM" : "AM";
  const displayH = h % 12 || 12;
  const dayLabel = day[0].toUpperCase() + day.slice(1, 3);
  return `${dayLabel}-${displayH}:${String(m).padStart(2, "0")} ${ampm}`;
}

const successMsg = (name) => (
  <Message showIcon type="success" header="Success">
    You successfully created {name || "the report"}.
  </Message>
);

const errMessage = (
  <Message showIcon type="error" header="Error">
    Something went wrong. Please try again.
  </Message>
);

/* ─────────────────────────────
   Component
   ───────────────────────────── */
export const NewReport = ({
  report,
  machines,
  update,
  setUpdate,
  field_name_tooltip,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();

  // console.log("\nfield_name_tooltip");
  // console.log(field_name_tooltip);

  // Modal open/close
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Form state
  const [reportName, setReportName] = useState("");
  const [fieldName, setFieldName] = useState(null);
  const [operator, setOperator] = useState(null);
  const [threshold, setThreshold] = useState(null);
  const [optionsUI, setOptionsUI] = useState(false);

  const [currentReportSys, setCurrentReportSys] = useState([]);
  const [reportScheduleDT, setReportScheduleDT] = useState([]);

  const [saving, setSaving] = useState(false);

  /* metadata from parent */
  const fieldOptions = useMemo(
    () => field_name_tooltip ?? [],
    [field_name_tooltip],
  );

  const selectedFieldDef = useMemo(
    () => fieldOptions.find((f) => f.value === fieldName) || null,
    [fieldOptions, fieldName],
  );

  const needsOperator = !!selectedFieldDef?.needsOperator;
  const hasThreshold = !!selectedFieldDef?.hasThreshold;
  const unit = selectedFieldDef?.unit ?? null;
  const operatorOptions = selectedFieldDef?.operatorOptions ?? [
    { label: "less_than", value: "less_than" },
    { label: "greater_than", value: "greater_than" },
  ];

  const th = selectedFieldDef?.threshold || {};
  const thresholdProps = {
    min: th.min ?? undefined,
    max: th.max ?? undefined,
    step: th.step ?? 0.1,
    postfix: unit === "percent" ? "%" : unit === "psi" ? "p" : undefined,
  };

  // When field changes, adjust operator/threshold defaults based on metadata
  useEffect(() => {
    if (!needsOperator) setOperator(null);
    if (!hasThreshold) {
      setThreshold(null);
    } else if (threshold == null && th?.default != null) {
      setThreshold(th.default);
    }
  }, [needsOperator, hasThreshold, th, threshold]);

  // Order systems so currently selected appear first
  const allSystems = useMemo(() => {
    if (!Array.isArray(machines) || machines.length === 0) return [];
    const selected = new Set(currentReportSys);
    const sel = machines.filter((m) => selected.has(m?.id));
    const rest = machines.filter((m) => !selected.has(m?.id));
    return [...sel, ...rest];
  }, [machines, currentReportSys]);

  const scheduleOptions = useMemo(
    () =>
      EMAIL_SLOTS.map((slot) => ({ value: slot, label: to12HrLabel(slot) })),
    [],
  );

  const TooltipOption = ({ label, tooltip }) => (
    <Whisper
      placement="top"
      trigger="hover"
      speaker={<Tooltip>{tooltip}</Tooltip>}
    >
      <span>{label}</span>
    </Whisper>
  );

  const onSubmit = useCallback(async () => {
    try {
      setSaving(true);
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const email_schedule = Object.fromEntries(
        (reportScheduleDT || []).map((t) => [t, true]),
      );

      const payload = {
        id: null,
        author: report?.author ?? null,
        report_name: reportName?.trim() || null,
        field_name: fieldName,
        operator,
        threshold,
        systems_list: Array.isArray(currentReportSys) ? currentReportSys : [],
        email_schedule,
      };

      await axiosInstance({
        method: "POST",
        url: `/user/reports/${payload.author}`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify(payload),
      });

      // Reset
      setReportName("");
      setFieldName(null);
      setOperator(null);
      setThreshold(null);
      setCurrentReportSys([]);
      setReportScheduleDT([]);

      setUpdate(!update);
      toaster.push(successMsg(payload.report_name), "topCenter");
      handleClose();
    } catch (error) {
      console.error(error);
      toaster.push(errMessage, "topCenter");
    } finally {
      setSaving(false);
    }
  }, [
    getAccessTokenSilently,
    report?.author,
    reportName,
    fieldName,
    operator,
    threshold,
    currentReportSys,
    reportScheduleDT,
    setUpdate,
    update,
    toaster,
  ]);

  return (
    <>
      <Button variant="primary" size="small" onClick={handleOpen}>
        Create Report
      </Button>

      <Modal size="xs" open={open} onClose={handleClose}>
        <Modal.Header>
          <Modal.Title>Create Report</Modal.Title>
        </Modal.Header>
        <Divider />
        <Modal.Body>
          <Form fluid>
            <Input
              label="Author"
              value={report?.author ?? ""}
              readOnly
              disabled
            />

            <Input
              label="Name"
              value={reportName}
              onChange={(value) => setReportName(value)}
              placeholder="Enter report name"
            />

            <Form.Group controlId="reportType">
              <Form.ControlLabel>Report Type</Form.ControlLabel>
              <Whisper
                placement="top"
                trigger="hover"
                speaker={
                  <Tooltip>
                    Choose the type of report you want to create. Each report
                    type provides different data insights.
                  </Tooltip>
                }
              >
                <div>
                  <Picker
                    data={fieldOptions}
                    value={fieldName}
                    searchable={false}
                    onChange={(value) => {
                      setFieldName(value);
                      // Find the selected item to preserve options_lists functionality
                      const selectedItem = fieldOptions.find(
                        (item) => item.value === value,
                      );
                      if (selectedItem) {
                        setOptionsUI(selectedItem.options_lists);
                        console.log("selected item:", selectedItem);
                      }
                    }}
                    placeholder="Select report type"
                    block
                  />
                </div>
              </Whisper>
            </Form.Group>

            {!optionsUI ? (
              // Only shows Email Schedule
              false
            ) : (
              <>
                {needsOperator && (
                  <Picker
                    data={operatorOptions}
                    value={operator}
                    searchable={false}
                    onChange={setOperator}
                    placeholder="Select condition"
                  />
                )}

                {hasThreshold && (
                  <Form.Group controlId="threshold">
                    <Form.ControlLabel>
                      Threshold{unit ? ` (${unit})` : ""}
                    </Form.ControlLabel>
                    <Stack>
                      <InputNumber
                        value={threshold}
                        onChange={setThreshold}
                        {...thresholdProps}
                      />
                    </Stack>
                  </Form.Group>
                )}
                {/* ▲▲▲ END REPLACED BLOCK ▲▲▲ */}

                <Form.ControlLabel>Systems List</Form.ControlLabel>
                <FilterSystems
                  report={report}
                  currentReportSys={currentReportSys}
                  setCurrentReportSys={setCurrentReportSys}
                  allSystems={allSystems}
                />
              </>
            )}
            <Picker
              data={scheduleOptions}
              value={reportScheduleDT}
              onChange={setReportScheduleDT}
              multiple={true}
              searchable={true}
              placeholder="Select email schedule times"
            />
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <FormActions
            onCancel={handleClose}
            onSubmit={onSubmit}
            cancelText="Cancel"
            submitText="Submit"
            isSubmitting={saving}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};
